'use client';
import { useEffect, useMemo, useRef } from 'react';
import { useUser } from '@/features/auth/shared/hooks/use-user';
import { useCurrencyStore } from '@/features/shared/currency-store';
import { useWtGamesAppDetails } from '@/features/games-app-bridge/shared/hooks/use-games-app-details';
import { CustomDataPayload } from '@/features/gtm/internal/types/gtm-datalayer';
import { LocaleCode } from '@/i18n/locales';
import { useLocale } from 'next-intl';
import { getGeoLocationOnClient } from '@/shared/utils/geo-location/get-geolocation-on-client';
import { LOCALE_MAP } from '@/shared/utils/locale-map';
import { useAppStore } from '@/features/shared/app-store';

export const GTMDatalayerProvider = () => {
  const { authenticatedUser, loading } = useUser();
  const currencyCode = useCurrencyStore((s) => s.currentCurrency);
  const setCustomData = useAppStore((s) => s.gtmSetCustomData);
  const gamesAppDetails = useWtGamesAppDetails();
  const dp = 'wildgames';
  const locale = useLocale() as LocaleCode;
  const country = getGeoLocationOnClient();

  //@Todo detect page type more reliable
  const payload: CustomDataPayload = useMemo(
    () => ({
      consoleVersion: gamesAppDetails?.gamesAppVersion ?? '',
      consoleChannel: gamesAppDetails?.gamesAppChannel ?? '',
      isWildMember: authenticatedUser?.isWildMember ?? false,
      currencyCode,
      ums: authenticatedUser?.umsGuid || undefined,
      pageType: '',
      page: {},
      source: gamesAppDetails?.isGamesApp ? 'GamesApp' : 'Web',
      userAgent: gamesAppDetails?.userAgent ?? '',
      userLanguage: LOCALE_MAP[locale],
      country: country?.countryISO,
      dp,
      brand: dp,
    }),
    [
      gamesAppDetails?.gamesAppVersion,
      gamesAppDetails?.gamesAppChannel,
      gamesAppDetails?.isGamesApp,
      gamesAppDetails?.userAgent,
      authenticatedUser?.isWildMember,
      authenticatedUser?.umsGuid,
      currencyCode,
      country,
      locale,
    ],
  );

  const prev = useRef<string>('');
  useEffect(() => {
    if (loading) return;

    const key = JSON.stringify(payload);
    if (prev.current === key) return;
    prev.current = key;
    window?.dataLayer?.push({
      event: 'custom-data',
      ...payload,
    });

    setCustomData(payload);
  }, [payload, authenticatedUser, gamesAppDetails, loading, setCustomData]);

  return null;
};
